// frontend/App.tsx
import React, { useEffect } from 'react';
import { Provider, useSelector, useDispatch } from 'react-redux';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import MainPage from './pages/MainPage';
import store, { RootState } from './store/store';
import { themes } from './themes/theme';
import * as pdfjs from 'pdfjs-dist';
import Auth0ProviderWithHistory from './auth/auth0-provider-with-history';
import { withAuthenticationRequired } from '@auth0/auth0-react';
import { WebSocketProvider } from './context/WebSocketContext';
import { v4 as uuidv4 } from 'uuid';
import { setSessionId, setOriginalSessionId } from './store/slices/sessionSlice';
import AuthenticationLoading from './components/ui/AuthenticationLoading';
import { LanguageProvider } from './context/LanguageContext';

const App: React.FC = () => {
  const mode = useSelector((state: RootState) => state.theme.mode);
  const theme = themes[mode];

  useEffect(() => {
    pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;
  }, []);

  return (
    <Provider store={store}>
      <Router>
        <Auth0ProviderWithHistory>
          <WebSocketProvider>
            <LanguageProvider>
              <ThemeProvider theme={theme}>
                <CssBaseline />
                <AppContent />
              </ThemeProvider>
            </LanguageProvider>
          </WebSocketProvider>
        </Auth0ProviderWithHistory>
      </Router>
    </Provider>
  );
};

const AppContent: React.FC = () => {
  const dispatch = useDispatch();

  // Initialize session_id when app starts
  useEffect(() => {
    const sessionId = uuidv4();
    console.log('Initializing new session:', sessionId);
    dispatch(setSessionId(sessionId));
    dispatch(setOriginalSessionId(sessionId)); // Keep original for voice operations
  }, [dispatch]);

  // Safari fix: Prevent browser from navigating to dropped files (especially PDFs)
  useEffect(() => {
    const preventFileNavigation = (e: DragEvent) => {
      // Prevent default for all drag events at document level
      if (e.dataTransfer && e.dataTransfer.items.length > 0) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    const preventDrop = (e: DragEvent) => {
      // Prevent default drop at document level
      // Our drop zones will handle the drop event explicitly
      const target = e.target as HTMLElement;
      // Only prevent if not dropping on our designated drop zones
      if (!target.closest('[data-drop-zone="true"]')) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    // Add listeners to document to catch all drag/drop events
    document.addEventListener('dragover', preventFileNavigation, false);
    document.addEventListener('drop', preventDrop, false);

    return () => {
      document.removeEventListener('dragover', preventFileNavigation, false);
      document.removeEventListener('drop', preventDrop, false);
    };
  }, []);

  return (
    <>
      <Routes>
        {/* Protect MainPage */}
        <Route path="/" element={<ProtectedMainPage />} />
        <Route path="/chat/:chatId" element={<ProtectedMainPage />} />
        {/* Additional routes can be added here */}
      </Routes>
    </>
  );
};

// Wrap MainPage to protect it
const ProtectedMainPage = withAuthenticationRequired(MainPage, {
  onRedirecting: () => <AuthenticationLoading />,
});

export default App;